import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import useCustomerList from '../../utils/useCustomerList'
import {
  getPaycodeCrosswalk, getCustomerPaycodeMapping, saveCustomerPaycodeMapping,
} from '../../api'
import './AdminPaycodeCrosswalk.css'

const STATUS_LABEL = {
  accepted: 'Accepted (name match)',
  accepted_by_evidence: 'Accepted (by evidence)',
  candidate_weak: 'Weak candidate',
  no_candidate: 'No candidate',
  no_wfm_volume: 'No WFM volume',
}

let rowIdCounter = 0
function withRowIds(rows) {
  return rows.map(r => ({ ...r, _id: ++rowIdCounter }))
}

export default function AdminPaycodeCrosswalk() {
  // ── Per-customer view/edit/confirm ──
  const { customers, error: customersError, reload: reloadCustomers } = useCustomerList(list => {
    if (list.length > 0) setSelectedCustomerId(list[0].customer_id || list[0].id)
  })
  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const [mapping, setMapping] = useState(null)   // { source, rows, refused }
  const [rows, setRows] = useState([])
  const [mapLoading, setMapLoading] = useState(false)
  const [mapError, setMapError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState(null)

  useEffect(() => {
    if (!selectedCustomerId) return
    loadMapping(selectedCustomerId)
  }, [selectedCustomerId])

  async function loadMapping(customerId) {
    setMapLoading(true); setMapError(null); setSaveMessage(null)
    try {
      const data = await getCustomerPaycodeMapping(customerId)
      if (data?.error) {
        setMapError(data.error)
        setMapping(null); setRows([])
      } else {
        setMapping(data)
        setRows(withRowIds(data.rows || []))
      }
    } catch (err) {
      setMapError(err.message || 'Failed to load mapping')
      setMapping(null); setRows([])
    } finally {
      setMapLoading(false)
    }
  }

  function updateRow(id, field, value) {
    setRows(prev => prev.map(r => (r._id === id ? { ...r, [field]: value } : r)))
  }

  function removeRow(id) {
    setRows(prev => prev.filter(r => r._id !== id))
  }

  function addRow() {
    setRows(prev => [...prev, ...withRowIds([{ wfm_code: '', pro_code: '', pass_to_payroll: '' }])])
  }

  async function saveMapping() {
    setSaving(true); setSaveMessage(null); setMapError(null)
    try {
      const payload = rows
        .filter(r => r.wfm_code.trim() && r.pro_code.trim())
        .map(r => ({ wfm_code: r.wfm_code.trim(), pro_code: r.pro_code.trim(), pass_to_payroll: r.pass_to_payroll.trim() }))
      const result = await saveCustomerPaycodeMapping(selectedCustomerId, payload)
      setSaveMessage(`Saved ${result.rows_saved} row(s). The 8 T&A Reconciliation metrics for this customer now compute against this mapping.`)
      await loadMapping(selectedCustomerId)
    } catch (err) {
      setMapError(err.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  // ── Bulk discovery preview (all tenants at once, read-only) ──
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)

  async function run() {
    setLoading(true); setError(null); setResult(null); setCopied(false)
    try {
      const data = await getPaycodeCrosswalk()
      if (data?.error) {
        setError(data.error)
      } else {
        setResult(data)
      }
    } catch (err) {
      setError(err.message || 'Derivation failed')
    } finally {
      setLoading(false)
    }
  }

  async function copyJson() {
    try {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard API unavailable
    }
  }

  const tenants = result?.tenants || {}
  const tenantIds = Object.keys(tenants).sort()

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Paycode Crosswalk' },
  ]
  const navActions = (
    <Link to="/admin" className="nav-btn">← Dashboard</Link>
  )

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />

      <div className="pcw-page">
        <div className="pcw-header">
          <div>
            <h1 className="pcw-title">Paycode Crosswalk</h1>
            <p className="pcw-subtitle">
              The WFM → Pro pay-code mapping the 8 T&amp;A Reconciliation metrics read.
              If a customer hasn't provided their own export, review the derived candidates
              below and confirm — that becomes the real mapping the moment you save it.
            </p>
          </div>
        </div>

        {/* ── Per-customer view/edit/confirm ── */}
        <div className="pcw-customer-panel">
          <div className="pcw-customer-row">
            <select
              className="pcw-select"
              value={selectedCustomerId}
              onChange={e => setSelectedCustomerId(e.target.value)}
            >
              {customers.map(c => (
                <option key={c.customer_id || c.id} value={c.customer_id || c.id}>
                  {c.company_name || c.name || c.customer_id}
                </option>
              ))}
            </select>
            {customersError && <LoadError inline what="customers" error={customersError} onRetry={reloadCustomers} />}
            {mapping?.source === 'saved' && (
              <span className="pcw-badge pcw-badge-saved">✓ Saved mapping — metrics are live</span>
            )}
            {mapping?.source === 'derived' && !mapping?.refused && (
              <span className="pcw-badge pcw-badge-derived">⚠ No mapping yet — showing derived candidates</span>
            )}
          </div>

          {mapLoading && <div className="pcw-empty-note">Loading…</div>}
          {mapError && <div className="pcw-error-banner">{mapError}</div>}
          {saveMessage && <div className="pcw-success-banner">{saveMessage}</div>}

          {mapping?.refused && (
            <div className="pcw-empty-note">
              Can't derive candidates for this customer yet — {mapping.refused}
            </div>
          )}

          {!mapLoading && mapping && !mapping.refused && (
            <>
              <table className="pcw-edit-table">
                <thead>
                  <tr>
                    <th>WFM Code</th>
                    <th>Pro Code</th>
                    <th>Pass to Payroll</th>
                    {mapping.source === 'derived' && <th>Status / Note</th>}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r._id}>
                      <td>
                        <input
                          className="pcw-input pcw-mono"
                          value={r.wfm_code}
                          onChange={e => updateRow(r._id, 'wfm_code', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="pcw-input pcw-mono"
                          value={r.pro_code}
                          onChange={e => updateRow(r._id, 'pro_code', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="pcw-input"
                          value={r.pass_to_payroll}
                          onChange={e => updateRow(r._id, 'pass_to_payroll', e.target.value)}
                          placeholder="Pass as Earnings Code"
                        />
                      </td>
                      {mapping.source === 'derived' && (
                        <td className="pcw-muted-cell">
                          {r.note || STATUS_LABEL[r.status] || r.status || ''}
                        </td>
                      )}
                      <td>
                        <button className="pcw-remove-btn" onClick={() => removeRow(r._id)} title="Remove row">✕</button>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={5} className="pcw-muted-cell" style={{ textAlign: 'center', padding: 16 }}>No rows yet — add one below.</td></tr>
                  )}
                </tbody>
              </table>
              <div className="pcw-edit-actions">
                <button className="nav-btn" onClick={addRow}>+ Add Row</button>
                <button className="nav-btn primary" onClick={saveMapping} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Mapping'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* ── Bulk discovery preview ── */}
        <div className="pcw-bulk-section">
          <div className="pcw-header">
            <div>
              <h2 className="pcw-subsection-title">Bulk Discovery Preview</h2>
              <p className="pcw-subtitle">
                See derived candidates for every tenant running both WFM and Pro at once,
                read-only — useful for spotting who needs a mapping before drilling into
                them individually above.
              </p>
            </div>
            <button className="nav-btn" onClick={run} disabled={loading}>
              {loading ? 'Deriving…' : 'Derive Crosswalk (all tenants)'}
            </button>
          </div>

          {error && <div className="pcw-error-banner">{error}</div>}

          {result && (
            <>
              <div className="pcw-toolbar">
                <div className="pcw-toolbar-note">
                  {tenantIds.length} tenant(s) — generated {result._metadata?.generated_at}
                </div>
                <button className="nav-btn" onClick={copyJson}>
                  {copied ? 'Copied!' : 'Copy Full JSON'}
                </button>
              </div>

              {tenantIds.map(cid => {
                const t = tenants[cid]
                if (t.refused) {
                  return (
                    <div key={cid} className="pcw-tenant-card pcw-refused">
                      <div className="pcw-tenant-id">{cid}</div>
                      <div className="pcw-refused-text">Skipped — {t.refused}</div>
                    </div>
                  )
                }
                return (
                  <div key={cid} className="pcw-tenant-card">
                    <div className="pcw-tenant-id">{cid}</div>
                    <div className="pcw-tenant-stats">
                      {t.pay_affecting?.length || 0} pay-affecting · {t.tracking_only?.length || 0} tracking-only
                      {t.axis_conflict?.length > 0 && ` · ${t.axis_conflict.length} axis conflict(s)`}
                    </div>
                    <table className="pcw-pairs-table">
                      <thead>
                        <tr>
                          <th>WFM Code</th>
                          <th>Pro Code</th>
                          <th>Status</th>
                          <th>Overlap</th>
                          <th>People</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(t.pairs || []).map((p, i) => (
                          <tr key={i} className={`pcw-status-${p.status}`}>
                            <td className="pcw-mono">{p.wfm_code}</td>
                            <td className="pcw-mono">{p.best?.pro_code || '—'}</td>
                            <td>{STATUS_LABEL[p.status] || p.status}</td>
                            <td>{p.best ? `${Math.round(p.best.overlap * 100)}%` : '—'}</td>
                            <td>{p.best?.people ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              })}
            </>
          )}

          {!result && !loading && !error && (
            <div className="pcw-empty-note">
              Click "Derive Crosswalk" for a read-only, all-tenant discovery pass.
            </div>
          )}
        </div>
      </div>
    </>
  )
}
